
import { useEffect, useState } from "react";
import { getColumns } from "./tableConfig";
import { Col, Row, Table, Button, Alert  } from 'antd';
import JsonDisplay from "../JSONDisplay";
import { RetweetOutlined } from '@ant-design/icons';
import { useAuth } from "../SessionContext";



/** Creates Action component column
 * @param {function} function will be executed when clicking action
 * 
 * @returns {object} jsx component including button config
 */
const getActionColumn = (onRetry, onCancel) => {
    // If action is Done then don't show buttons
    const actionColumn = {
    title: 'Event Action',
    dataIndex: 'action',
    key: 'action',
    render: (_, record) => {
        return (
            <>
            <Button style={{ marginRight: '0.5rem' }}id="process" color="cyan" onClick={()=> { onRetry(record)} } variant="solid">
                Retry
                </Button>
                <Button id="cancel" color="danger" variant="dashed" onClick={()=> { onCancel(record) }}>
                    Cancel
                </Button>
                </>
        )
    }
    };
    return actionColumn;
}

/** Creates Event component column
 * @param {function} function will be executed when clicking payload
 * 
 * @returns {object} jsx component that renders the payload in button
 */
const getEventPayloadColumn = (setPayloadDisplay) => {
    const eventPayloadColumn = {
    title: 'Event Payload',
    dataIndex: 'body_event',
    key: 'body_event',
    ellipsis: true,
    render: (_, { body_event }) => {
        return (
            <>
                <Button 
                 id="showEvent"
                 color="primary"
                 variant="text"
                 onClick={() => {
                    setPayloadDisplay(body_event);
                }} >
                    {JSON.stringify(body_event)}
                </Button>
                </>
        )
    }
    }
    return eventPayloadColumn;
}
/**
 * Events Table Handles the connection with pending events
 *
 * @returns {JSX.Element} returns whole table
 */
export default function EventsTable({ isMobile }) {
    const [data, setData] = useState([]);
    const [payloadDisplay, setPayloadDisplay] = useState('');
    const [toast, setToast] = useState(null);
    const [loading, setLoading] = useState(false);
    const { accessToken } = useAuth();

    const onRetry = (payload) => {
        const headers = {'Authorization': `Bearer ${accessToken}`};
        fetch('/api/v1/events', { method: 'POST', body: JSON.stringify(payload), headers })
            .then((payload) => {
                payload.json().then((response) => {
                const [ successRow ] = response;
                setData((prev) => {
                    return prev.filter((row)=> row.id !== successRow.id)
                })
                const description =  `Event for: ${successRow.appName} has been cancelled`;
                setPayloadDisplay('');
                setToast({ type: 'success', message: 'Success', description });
                })
            })
            .catch((e) => {
                setToast(e.message);
            })
    }

    const onCancel = (payload) => {
        const idBody = { id: payload.id };
        const headers = {'Authorization': `Bearer ${accessToken}`};
        fetch('/api/v1/events', { method: 'PUT', body: JSON.stringify(idBody), headers })
            .then((payload) => {
                if(!payload.ok) {
                    payload.text().then((msg) => {
                    setLoading(false);
                    setToast({type: 'error', message: `Error ${JSON.stringify(msg)}`});
                    return;
                    })
                }
                payload.json().then((response) => {
                const [ cancelledRow ] = response;
                setData((prev) => {
                    return prev.filter((row)=> row.id !== cancelledRow.id)
                })
                const description =  `Event for: ${cancelledRow.app_name} has been cancelled`;
                setPayloadDisplay('');
                setToast({ type: 'success', message: 'Success', description });
                })
            })
            .catch((e) => {
                setToast(e.message);
            });
    }
    useEffect(()=> {
        setLoading(true);
        if(accessToken) {
        const headers = {'Authorization': `Bearer ${accessToken}`};
        fetch('/api/v1/events', { headers }).then((response) => {
            if(!response.ok) {
                    response.text().then((msg) => {
                    setToast({type: 'info', message: `Information ${JSON.stringify(msg)}`});
                    setLoading(false);
                    })
                return;
            }
            response.json().then((payload)=>{
            setData(payload);            })
            setLoading(false);
        }).catch((e) => {
            setToast(()=> ({ type: 'error', message: e.message, description: "Couldn't call service" }));
            setLoading(false);
        });
        }
    }, [accessToken]);

    const handleRefreshButton = () => {
        const headers = {'Authorization': `Bearer ${accessToken}`};
        fetch('/api/v1/events', { headers }).then((payload) => {
            if(!payload.ok){
                    payload.text().then((msg) => {
                    setToast({type: 'info', message: `Information: ${JSON.stringify(msg)}`});
                    setLoading(false);
                    })
                    return;
            }
            payload.json().then((response) => {
            setData(response);
            });
        }).catch((e) => {
            setToast(()=> ({ type: 'error', message: e.message, description: "Couldn't call service" }));
        });
    }

    const actionColumn = getActionColumn(onRetry, onCancel);
    const eventColumn = getEventPayloadColumn(setPayloadDisplay);
    return (
    <>
    { toast && (<Alert style={{marginBottom: '1rem'}} {...toast} showIcon banner closable onClose={()=>{
        setToast(null);
    }}/>) }
      <Row style={{ marginBottom: '1rem' }} gutter={[16, 16]}>
        <Col xs={24} sm={12} md={8} lg={6}>
          <Button 
            type="primary"
            icon={<RetweetOutlined />}  
            size={isMobile ? 'middle' : 'large'}
            onClick={handleRefreshButton}
            block={isMobile}
          >
            {!isMobile && 'Refresh'}
          </Button>
        </Col>
      </Row>
      
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <Table
            loading={loading}
            pagination={{ 
              pageSize: 5, 
              total: data.length,
              simple: isMobile,
              showSizeChanger: !isMobile
            }}
            columns={getColumns(actionColumn, eventColumn)}
            dataSource={data}
            scroll={isMobile ? { x: true } : undefined}
            size={isMobile ? 'small' : 'middle'}
          />
        </Col>
      </Row>
      
      <Row gutter={[16, 16]}>
        <Col xs={24}>
          <h4>Current Event Payload:</h4>
          <div style={{ overflowX: 'auto' }}>
            <JsonDisplay data={payloadDisplay} />
          </div>
        </Col>
      </Row>
    </>
    )
}